import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

import { bookFilterFrom, bookSearchFrom, type BookFilter } from './bookFilter'
import { keepingView } from './tableAddress'

/**
 * A book page's filter, kept in its address (TRKC-97). The controls answer from state the
 * page owns, with the address as its copy — the board's reason (TRKC-90): the router applies
 * an address change inside a transition, and controls fed straight from it showed their old
 * value for a moment. The address can also change under the page — the menu's plain link,
 * the browser's Back — and the filter follows it; following its own writes changes nothing.
 */
export function useBookFilter() {
  const [searchParams, setSearchParams] = useSearchParams()
  // The book's own filters as the address states them, apart from the column view kept beside
  // them (TRKC-110): sorting a heading or typing in a column's box rewrites the address, and
  // that is no change of the book's filters — read as one, it set a search typed and not yet
  // sent back to the last one sent (Copilot on #59).
  const address = bookSearchFrom(bookFilterFrom(searchParams)).toString()
  const [filter, setFilter] = useState(() => bookFilterFrom(searchParams))
  // What the search box holds, apart from the filter: it searches when it is sent, not as
  // it is typed — the books' search has always had its button.
  const [searchText, setSearchText] = useState(filter.search ?? '')
  // What the state box holds: a whole state narrows the book, one letter is half of one and
  // narrows nothing — the board's rule, so the book is never narrowed by a state the box no
  // longer shows.
  const [stateText, setStateText] = useState(filter.state ?? '')

  useEffect(() => {
    const next = bookFilterFrom(new URLSearchParams(address))
    setFilter(next)
    setSearchText(next.search ?? '')
    setStateText((typed) => (next.state === undefined && typed.length < 2 ? typed : (next.state ?? '')))
  }, [address])

  /**
   * Narrows the book, with the search box's words going along on every change: what the bar
   * shows is what the list is narrowed to, never a search typed and silently left out.
   */
  const narrow = (changes: Partial<BookFilter>) => {
    const next = { ...filter, search: searchText.trim() || undefined, ...changes }
    setFilter(next)
    // The column view the address holds goes along (TRKC-110).
    setSearchParams((current) => keepingView(bookSearchFrom(next), current), { replace: true })
  }

  const typeState = (value: string) => {
    const state = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2)
    const whole = state.length === 2 ? state : undefined
    setStateText(state)

    if (whole !== filter.state) {
      narrow({ state: whole })
    }
  }

  return { filter, searchText, setSearchText, stateText, typeState, narrow }
}

export type BookFilterControls = ReturnType<typeof useBookFilter>
